import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home, RefreshCcwIcon, BadgeCheckIcon } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/solid';
import * as SolidIcons from '@heroicons/react/24/solid';
import * as OutlineIcons from '@heroicons/react/24/outline';
import * as LucideIcons from "lucide-react";

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const BalanceTransferLoan = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Helper to resolve icon component
  const getIconComponent = (iconName) => {
    if (!iconName) return Star; // Default
    // Check Lucide
    if (LucideIcons[iconName]) return LucideIcons[iconName];
    // Check Heroicons Solid
    if (SolidIcons[iconName]) return SolidIcons[iconName];
    // Check Heroicons Outline
    if (OutlineIcons[iconName]) return OutlineIcons[iconName];
    return Star;
  };

  // Matching icons & colors for each item (fallback)
  const fallbackIcons = [
    { icon: <User />, from: "from-blue-500", to: "to-cyan-400" },
    { icon: <Globe />, from: "from-green-500", to: "to-lime-400" },
    { icon: <Briefcase />, from: "from-pink-500", to: "to-rose-400" },
    { icon: <DollarSign />, from: "from-orange-500", to: "to-amber-400" },
    { icon: <FileText />, from: "from-purple-500", to: "to-indigo-400" },
    { icon: <Shield />, from: "from-teal-500", to: "to-emerald-400" },
    { icon: <Star />, from: "from-yellow-500", to: "to-orange-400" },
    { icon: <Ruler />, from: "from-red-500", to: "to-orange-400" },
  ];



  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Overview state
  const [overview, setOverview] = useState(null);

  // Key Benefits state
  const [benefits, setBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState(null);

  // Eligibility state
  const [eligibility, setEligibility] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState(null);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsSettings, setDocumentsSettings] = useState(null);

  // Interest Rates state
  const [interestRates, setInterestRates] = useState([]);
  const [interestRatesSettings, setInterestRatesSettings] = useState(null);

  // FAQs state
  const [faqs, setFaqs] = useState([]);
  const [faqSettings, setFaqSettings] = useState(null);

  // CTA Banner state
  const [ctaBanner, setCtaBanner] = useState(null);

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Reduce Your EMIs with Aham Balance Transfer Loan',
    subtitle: 'Transfer your existing home loan to Aham for lower interest rates, better terms, and optional top-up funding.',
    image_url: 'img/loan-banners/Balance-Transfer-Banner.webp',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    highlights: []
  };

  // Scroll to section function


  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch banner data
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/balance_transfer_loan');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          setBanner(banners[0]);
        } else {
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Fetch overview data
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await axios.get('/api/admin/loan-overview/balance_transfer_loan');
        if (response.data.success && response.data.data) {
          setOverview(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
      }
    };

    fetchOverview();
  }, []);

  // Fetch Key Benefits data
  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/balance_transfer_loan'),
          axios.get('/api/admin/key-benefits/settings/balance_transfer_loan')
        ]);
        if (benefitsRes.data.success) {
          setBenefits(benefitsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching benefits:', error);
      }
    };

    fetchBenefits();
  }, []);

  // Fetch Eligibility data
  useEffect(() => {
    const fetchEligibility = async () => {
      try {
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/eligibility/balance_transfer_loan'),
          axios.get('/api/admin/eligibility/settings/balance_transfer_loan')
        ]);
        if (criteriaRes.data.success) {
          setEligibility(criteriaRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setEligibilitySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility:', error);
      }
    };

    fetchEligibility();
  }, []);

  // Fetch Documents data
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const [docsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/documents/balance_transfer_loan'),
          axios.get('/api/admin/documents/settings/balance_transfer_loan')
        ]);
        if (docsRes.data.success) {
          setDocuments(docsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };

    fetchDocuments();
  }, []);

  // Fetch Interest Rates data
  useEffect(() => {
    const fetchInterestRates = async () => {
      try {
        const [ratesRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/interest-rates/balance_transfer_loan'),
          axios.get('/api/admin/interest-rates/settings/balance_transfer_loan')
        ]);
        if (ratesRes.data.success) {
          setInterestRates(ratesRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setInterestRatesSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates:', error);
      }
    };

    fetchInterestRates();
  }, []);

  // Fetch FAQs data
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/faqs/balance_transfer_loan'),
          axios.get('/api/admin/faqs/settings/balance_transfer_loan')
        ]);
        if (faqsRes.data.success) {
          setFaqs(faqsRes.data.data || []);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setFaqSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    };

    fetchFAQs();
  }, []);

  // Fetch CTA Banner data
  useEffect(() => {
    const fetchCTABanner = async () => {
      try {
        const response = await axios.get('/api/admin/cta-banner/balance_transfer_loan');
        if (response.data.success && response.data.data) {
          setCtaBanner(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner:', error);
      }
    };

    fetchCTABanner();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };
            
             // Schema for SEO
  const balanceTransferSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/home-loan-balance-transfer/#webpage",
        "url": "https://ahamhfc.com/home-loan-balance-transfer",
        "name": "Home Loan Balance Transfer - Aham Housing Finance",
        "description": "Transfer your existing home loan to Aham Housing Finance for lower interest rates, reduced EMIs, better terms, and optional top-up funding.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/home-loan-balance-transfer/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/home-loan-balance-transfer/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Balance-Transfer-Overview-1768306447303-505264587.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/home-loan-balance-transfer/#loan",
        "name": "Balance Transfer Loan",
        "alternateName": "Home Loan Balance Transfer",
        "url": "https://ahamhfc.com/home-loan-balance-transfer",
        "image": {
          "@id": "https://ahamhfc.com/home-loan-balance-transfer/#primaryimage"
        },
        "description": "Transfer an existing home loan to Aham Housing Finance to reduce EMI burden, access lower interest rates, and get better loan terms with optional top-up funding.",
        "category": "Housing Finance",
        "loanType": "Home Loan Balance Transfer",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Existing home loan borrowers seeking lower EMIs or better terms"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/home-loan-balance-transfer/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Balance Transfer Loan",
            "item": "https://ahamhfc.com/home-loan-balance-transfer"
          }
        ]
      }
    ]
  };




  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Home Loan Balance Transfer: Save on EMIs | Aham Housing Finance"
        description="Switch to Aham Housing Finance for a home loan balance transfer with attractive interest rates. Use our calculator to see your savings. Quick & easy process!"
        keywords="Aham balance transfer loan, Aham Housing Finance balance transfer loan, home loan balance transfer, balance transfer home loan, transfer home loan to Aham, reduce EMI home loan, lower interest rate home loan, home loan refinance India, housing loan balance transfer, mortgage balance transfer, affordable balance transfer loan, balance transfer loan up to 25 lakhs, tenure up to 20 years balance transfer, better EMI options home loan, switch home loan lender, self employed balance transfer loan, low documentation balance transfer loan, no income proof balance transfer loan, Aham refinance home loan, Aham Housing Finance refinance"
        canonical="https://ahamhfc.com/home-loan-balance-transfer"
        ogTitle="Home Loan Balance Transfer: Save on EMIs | Aham Housing Finance"
        ogDescription="Switch to Aham Housing Finance for a home loan balance transfer with attractive interest rates. Use our calculator to see your savings. Quick & easy process!"
       schema={balanceTransferSchema}
           />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url || 'img/loan-banners/Balance-Transfer-Banner.webp'})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
               

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || "Reduce Your EMIs with Aham Balance Transfer Loan"}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || "Transfer your existing home loan to Aham for lower interest rates, better terms, and optional top-up funding."}
                </p>

                {/* Display highlights if available */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <a href={banner?.primary_button_link || "https://aham.mbwhost.in/contact"} className="w-full sm:w-auto text-left">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                    >
                      {banner?.primary_button_text || "Apply Now"}
                    </motion.button>
                  </a>

                  {banner?.secondary_button_text && (
                    <a href={banner.secondary_button_link || "#"} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                
                 {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Balance Transfer Loan</span>
                </motion.div>
                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
      <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left ">
                {overview?.title || "Balance Transfer Loan"}
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  {overview?.description || "Reduce your EMI burden and enjoy better loan terms by transferring your existing home loan to Aham Housing Finance. Our Balance Transfer Loan helps you move your outstanding principal from another financial institution to Aham with the option to avail a top-up for construction, extension, or home improvements."}
                </p>

                <ul className="space-y-3">
                  {(() => {
                    let features = [
                      'Loan from ₹5 lakhs to ₹25 lakhs – Designed for both mid-size and large outstanding amounts.',
                      'Tenure up to 20 years – Allows better EMI planning and long-term affordability.',
                      'Minimum 12-month loan vintage – Ensures stability and eligibility for takeover.',
                      'Top-up available for construction/renovation – Additional funds for your home improvement needs.'
                    ];

                    if (overview?.features) {
                      try {
                        features = JSON.parse(overview.features);
                      } catch (e) {
                        console.error('Error parsing features:', e);
                      }
                    }

                    return features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 mt-1" />
                        <span dangerouslySetInnerHTML={{ __html: feature }}></span>
                      </li>
                    ));
                  })()}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || "img/loan-overview/Balance-Transfer-Overview.webp"}
                alt="Aham Housing Finance expert explaining home loan balance transfer benefits and attractive interest rates to a customer"
                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>

          </div>
        </div>
      </section>




      {/* Key Benefits Section */}
      < section id="benefits" className="relative py-24 overflow-hidden" >

        {/* 🔥 Background – gradient + subtle pattern */}
        < div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div >

        {/* Dark gradient overlay */}
        < div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white" ></div >

        <div className="relative container mx-auto px-4">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6 leading-tight">
              {benefitsSettings?.section_title || "Key Benefits & Features"}
            </h2>
            <p className="text-lg md:text-xl text-white mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings?.section_subtitle || "Discover why Aham Balance Transfer Loan is the right choice for you"}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {(Array.isArray(benefits) && benefits.length > 0 ? benefits.filter(b => b.is_active !== 0) : [
              { title: "Lower Interest Rates", description: "Reduce your monthly EMIs by transferring your existing loan to Aham at competitive rates.", icon_name: "RefreshCcwIcon" },
              { title: "Top-Up Facility Available", description: "Get additional funds for home improvement, extension, or construction if eligible.", icon_name: "BanknotesIcon" },
              { title: "Flexible Tenure up to 20 Years", description: "Restructure your repayment tenure to match your financial comfort.", icon_name: "ClockIcon" },
              { title: "Quick and Secure Transfer", description: "Fast approval and seamless coordination with your existing lender for smooth takeover.", icon_name: "ShieldCheckIcon" },
              { title: "FOIR & LTV as per Home Loan Norms", description: "Balanced and responsible funding aligned with Aham's financial assessment standards.", icon_name: "ChartBarIcon" },
              { title: "No Hidden Charges", description: "Transparent processing with clear guidelines on fees, documentation, and timelines.", icon_name: "BadgeCheckIcon" }
            ]).map((benefit, index) => {
              const IconComponent = getIconComponent(benefit.icon_name);
              const isDynamic = !!benefit.icon_name;

              return (
                <motion.div
                  key={benefit.id || index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
                >
                  {/* Floating Glow */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500 rounded-full blur-3xl opacity-20"></div>

                  {/* Icon */}
                  <div className={`
                    w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg
                    bg-gradient-to-br 
                    ${benefit.gradient_from?.startsWith('from-') ? benefit.gradient_from : `from-${benefit.gradient_from || 'primary-600'}`} 
                    ${benefit.gradient_to?.startsWith('to-') ? benefit.gradient_to : `to-${benefit.gradient_to || 'primary-400'}`}
                  `}>
                    <IconComponent className="h-9 w-9 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-700 leading-relaxed font-medium">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Button */}
          <div className="mt-16 flex justify-center">
            <a href={benefitsSettings?.cta_link || "/contact"} className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {benefitsSettings?.cta_text || "Request a Quote"}
              </motion.button>
            </a>
          </div>
        </div>
      </section >

      {/* Eligibility Criteria Section */}
      < section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden" >

        {/* Soft Background Blobs */}
        < div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full" ></div >
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight"
              >
                {eligibilitySettings?.section_title || "Eligibility Criteria"}
              </motion.h2>

              <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
                {eligibilitySettings?.section_subtitle || "Find out if you're eligible for Aham Balance Transfer Loan"}
              </p>
            </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(eligibility.length > 0 ? eligibility.filter(e => e.is_active !== 0) : [
              { criteria: "Minimum existing loan vintage of 12 months with the current lender", icon_name: "User", gradient_from: "from-blue-500", gradient_to: "to-cyan-400" },
              { criteria: "Property must be fully constructed or under construction for not more than 12 months", icon_name: "Globe", gradient_from: "from-green-500", gradient_to: "to-lime-400" },
              { criteria: "Loan amount eligibility based on income, FOIR norms, and repayment capacity", icon_name: "Briefcase", gradient_from: "from-pink-500", gradient_to: "to-rose-400" },
              { criteria: "FOIR typically capped at 50% depending on income profile", icon_name: "DollarSign", gradient_from: "from-orange-500", gradient_to: "to-amber-400" },
              { criteria: "LTV norms to match underlying home loan category", icon_name: "FileText", gradient_from: "from-purple-500", gradient_to: "to-indigo-400" },
              { criteria: "No takeover of pure plot loans unless immediate construction is planned", icon_name: "Shield", gradient_from: "from-teal-500", gradient_to: "to-emerald-400" }
            ]).map((item, index) => {
              const isDynamic = !!item.id;
              const text = item.criteria;

              let IconComp, fromColor, toColor;

              if (isDynamic) {
                IconComp = getIconComponent(item.icon_name);
                fromColor = item.gradient_from?.startsWith('from-') ? item.gradient_from : `from-${item.gradient_from || 'blue-500'}`;
                toColor = item.gradient_to?.startsWith('to-') ? item.gradient_to : `to-${item.gradient_to || 'cyan-400'}`;
              } else {
                const iconData = fallbackIcons[index % fallbackIcons.length];
                IconComp = () => iconData.icon;
                fromColor = iconData.from;
                toColor = iconData.to;
              }

              return (
                <motion.div
                  key={isDynamic ? item.id : index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="
                  group backdrop-blur-xl bg-white/70 
                  border border-white/40 shadow-xl rounded-2xl 
                  p-6 flex flex-col items-center text-center gap-5 
                  hover:-translate-y-2 hover:shadow-2xl 
                  transition-all duration-300
                "
                >
                  {/* Gradient Icon */}
                  <div
                    className={`
                    w-14 h-14 rounded-xl flex items-center justify-center 
                    bg-gradient-to-br ${fromColor} ${toColor}
                    text-white shadow-lg group-hover:scale-105 transition
                  `}
                  >
                    {isDynamic ? <IconComp className="h-7 w-7" /> : fallbackIcons[index % fallbackIcons.length].icon}
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold text-lg leading-snug">
                      {text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section >

      {/* Documents Required Section */}
      < section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden" >

        {/* Background blur circles */}
        < div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full" ></div >
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-5xl font-extrabold text-gray-900 mb-4"
            >
              {documentsSettings?.section_title || "Documents Required"}
            </motion.h2>

            <p className="text-lg text-gray-600 mt-3">
              {documentsSettings?.section_subtitle || "Keep these documents ready for a smooth application process"}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(() => {
                  // Group documents by category or use fallback
                  const docsByCategory = {};
                  if (Array.isArray(documents) && documents.length > 0) {
                    // Filter active documents only
                    const activeDocuments = documents.filter(doc => doc.is_active !== 0);
                    activeDocuments.forEach(doc => {
                      if (!docsByCategory[doc.category]) {
                        docsByCategory[doc.category] = [];
                      }
                      docsByCategory[doc.category].push(doc.document_name);
                    });
                  } else {
                    // Fallback static data
                    docsByCategory.common = [
                      "Completed loan application form with passport-size photographs",
                      "Identity Proof – Aadhaar, PAN, Passport, Driving License",
                      "Address Proof – Aadhaar, Passport, Utility Bills, Rent Agreement",
                      "Bank statements for last 6 months"
                    ];
                    docsByCategory.salaried = [
                      "Last 3 months salary slips",
                      "6 months bank statement showing salary credits",
                      "Form 16 or ITR for last 2 years",
                      "Employee ID Card"
                    ];
                    docsByCategory.selfEmployed = [
                      "Business registration proof – GST, TIN, Trade License",
                      "Audited financial statements / ITR (last 2–3 years)",
                      "Business bank account statements (last 12 months)",
                      "Business License / Trade License"
                    ];
                    docsByCategory.nri = [
                      "Valid passport and visa",
                      "Overseas employment contract and salary proof",
                      "NRE/NRO bank statements (last 6 months)",
                      "Power of Attorney (if applicable)"
                    ];
                    docsByCategory.property = [
                      "Property sale deed / title documents",
                      "Approved building plan and construction status report",
                      "Original property documents and no-dues letter from existing lender",
                      "Loan account statement from existing lender",
                      "List of original documents held by current lender",
                      "NOC from Society / Local Authority"
                    ];
                  }

                  const currentDocs = docsByCategory[activeTab] || [];
                  return currentDocs.map((doc, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                      className="
                  flex items-start gap-4 
                  p-5 bg-gradient-to-br from-white to-gray-50 
                  rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
                "
                    >
                      <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                        <FileText className="h-6 w-6" />
                      </div>
                      <span className="text-gray-900 font-semibold">{doc}</span>
                    </motion.li>
                  ));
                })()}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section >


      {/* Interest Rates Section */}
      < section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        < div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40" ></div >
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestRatesSettings?.section_title || "Affordable Home Loan Interest Rates"}
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestRatesSettings?.intro_text || "Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home construction affordable and stress-free."}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {(Array.isArray(interestRates) && interestRates.length > 0 ? interestRates.filter(r => r.is_active !== 0) : [
                  { icon: "🏠", title: "Flexible Rates", description: "Rates tailored based on loan amount, tenure, credit profile & location." },
                  { icon: "🔍", title: "100% Transparency", description: "No hidden fees. No surprise charges. Complete clarity from start to finish." },
                  { icon: "🤝", title: "Dedicated Support", description: "Our expert team guides you at every stage of your loan journey." }
                ]).map((rate, index) => (
                  <motion.div
                    key={rate.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 + (index * 0.2) }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">{rate.icon}</div>
                    <h4 className="text-xl font-semibold mb-2">{rate.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {rate.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestRatesSettings?.closing_text || "Begin your home construction journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs."}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestRatesSettings?.cta_link || "https://aham.mbwhost.in/emi-calculator"}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                {interestRatesSettings?.cta_text || "Calculate EMI"}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section >


      {/* FAQ Section */}
      < section id="faq" className="py-20 bg-white" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {faqSettings?.section_title || "Frequently Asked Questions"}
            </h2>
            <p className="text-lg text-gray-600">
              {faqSettings?.section_subtitle || "Find answers to common questions about Balance Transfer Loan"}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {(Array.isArray(faqs) && faqs.length > 0 ? faqs.filter(f => f.is_active !== 0) : [
              {
                question: "What is a Balance Transfer Loan?",
                answer: "A Balance Transfer Loan allows you to transfer your existing home loan from another lender to Aham Housing Finance to enjoy better interest rates and improved repayment terms."
              },
              {
                question: "Can I get a top-up loan along with balance transfer?",
                answer: "Yes. Subject to eligibility, you may get additional funding for home improvement, extension, or construction."
              },
              {
                question: "What is the minimum loan vintage required for transfer?",
                answer: "A minimum of 12 months of repayment with the existing lender is required for balance transfer eligibility."
              },
              {
                question: "Is balance transfer allowed for plot loans?",
                answer: "No. Plot-only loans are not eligible unless immediate construction is planned and initiated."
              },
              {
                question: "What are the loan tenure options?",
                answer: "The repayment tenure ranges from 1 year to 20 years depending on eligibility and loan category."
              },
              {
                question: "What are the FOIR and LTV norms?",
                answer: "FOIR is generally capped at 50%, and LTV is applied based on the underlying home loan norms."
              },
              {
                question: "What documents are required from my existing lender?",
                answer: "A no-dues certificate, loan account statement, original property documents list, and foreclosure letter are required during takeover."
              }
            ]).map((faq, index) => (
              <motion.div
                key={faq.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                    ? 'bg-primary-50 border-2 border-primary-600'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {activeFAQ === index ? (
                      <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {activeFAQ === index && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 text-gray-700 leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section >

      {/* CTA Banner */}
      < section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden" >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBanner?.title || "Ready to Reduce Your EMIs?"}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBanner?.description || "Take the first step towards better loan terms with Aham's hassle-free balance transfer process"}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaBanner?.button_link || "https://aham.mbwhost.in/contact"}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBanner?.button_text || "Talk to Our Loan Expert"}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section >


    </div >
  );
};

export default BalanceTransferLoan;